import Plyr from 'plyr';
import { setCookie, getCookie } from '../lib/utils.js';

class PlyrRunner {
	constructor(el) {
		this.el = el;
		this.redirectModal = document.querySelector('#plyr-redirect-modal');
		this.redirectCountdown = document.querySelector(
			'#plyr-redirect-countdown'
		);
		this.timeToRedirect = 4; // In seconds

		this.initPlyr();
	}

	initPlyr() {
		this.options = this.getDefaults();
		this.source = this.getSource();
		this.options.controls = this.getControls();
		console.log('test');

		// Init Plyr
		this.player = new Plyr(this.el, this.options);

		this.player.on('ready', () => {
			// Set poster when the player has loaded,
			// we do this so posters load for Vimeo and YouTube.
			if (this.source.poster) {
				this.player.poster = this.source.poster;
			}
		});

		this.player.on('ended', () => {
			this.player.fullscreen.exit();
			this.player.source = this.source; // Reset the source so the player can be reinitialized.
			this.handleRedirect();
		});

		// If there is an alternate source, add support for it.
		if (this.source.source_alt) {
			// Set source urls
			this.url_src_default = this.source.sources[0].src;
			this.url_src_alt = this.source.source_alt;

			const audioDescToggler = document.querySelector(
				`[data-plyr="audio-desc"]`
			);

			document.addEventListener('click', e => {
				if (e.target.dataset.plyr !== audioDescToggler.dataset.plyr)
					return;

				e.preventDefault();
				const switchToAlt = this.player.source === this.url_src_default;
				// Get the current time, so we can skip to that time when we switch to the alternate source.
				const prevTime = this.player.currentTime;

				// Replace the source url for the player with the alternate source url.
				this.source.sources[0].src = switchToAlt
					? this.url_src_alt
					: this.url_src_default;

				// Update the source
				this.player.source = this.source;

				// When the player is ready, play and seek to the previous time.
				this.player.on('ready', () => {
					// Play
					this.player.play();
					// Update the icon state.

					this.handleCustomControlIconState(
						audioDescToggler,
						switchToAlt
					);
					// Set the icon state
					this.player.once('play', () => {
						this.player.currentTime = prevTime;
					});
				});

				// Set a cookie to remember the user's preference
				setCookie(
					'plyr_alt_source',
					this.player.source === this.url_src_default,
					30
				);
			});

			// If the user has previously selected to use the alternate source, use that source.
			if (getCookie('plyr_alt_source') === 'true') {
				this.handleCustomControlIconState(audioDescToggler, true);
				this.source.sources[0].src = this.url_src_alt;
			}
		}

		// Set source
		this.player.source = this.source;
	}

	handleCustomControlIconState(controlEl, isActive) {
		this.player.once('ready', () => {
			// Get element by id, since the elements may be reloaded by Plyr.
			const controlElId = controlEl.getAttribute('data-plyr');
			const icon = document
				.querySelector(`[data-plyr="${controlElId}"]`)
				.querySelector('.icon');

			if (isActive === true) {
				icon.classList.remove('icon--not-pressed');
				icon.classList.add('icon--pressed');
			} else {
				icon.classList.remove('icon--pressed');
				icon.classList.add('icon--not-pressed');
			}
		});
	}

	handleRedirect() {
		if (!this.source.redirect_on_end) return;

		// Show the modal
		this.redirectModal.classList.add('video-redirect-overlay--is-visible');

		let timeToRedirect = this.timeToRedirect;
		// Start countdown in modal
		this.redirectCountdown.innerHTML = timeToRedirect;
		this.redirectTimer = setInterval(() => {
			this.redirectCountdown.innerHTML = --timeToRedirect;
		}, 1000);

		// Redirect after countdown
		this.redirectTimeout = setTimeout(() => {
			this.resetRedirect(); // Reset everything before redirecting in case the user dicides to go back in browser history.
			window.location.href = this.source.redirect_on_end;
		}, timeToRedirect * 1000);

		// Handle button clicks in modal
		document.querySelectorAll('[data-plyr-event]').forEach(el => {
			el.addEventListener('click', e => {
				e.preventDefault();
				this.resetRedirect(); // Reset everything before redirecting in case the user dicides to go back in browser history.

				switch (e.target.getAttribute('data-plyr-event')) {
					case 'replay':
						this.player.play();
						break;
					case 'redirect':
						window.location.href = this.source.redirect_on_end;
						break;
				}
			});
		});
	}

	resetRedirect() {
		clearInterval(this.redirectTimer);
		clearTimeout(this.redirectTimeout);
		this.redirectModal.classList.remove(
			'video-redirect-overlay--is-visible'
		);
	}

	getSource() {
		return window[`plyr_source_${this.el.id}`];
	}

	getDefaults() {
		return {
			youtube: {
				noCookie: true,
				rel: 0,
				showinfo: 0,
				ecver: 2,
				iv_load_policy: 3,
				modestbranding: 1,
				controls: 0,
			},
			vimeo: {
				byline: false,
				portrait: false,
				title: false,
				speed: false,
				transparent: false,
				dnt: true,
			},
			// fullscreen: {
			// 	enabled: false,
			// 	fallback: true,
			// 	iosNative: true,
			// 	container: null,
			// },
			controls: ['play-large', 'play', 'captions'],
			resetOnEnd: true,
		};
	}

	getControls() {
		let controls = `
		<button aria-label="Play" class="plyr__control plyr__control--overlaid" data-plyr="play" type="button"><svg aria-hidden="true">
		<use xlink:href="#plyr-play"></use></svg><span class="plyr__sr-only">Play</span></button>

		<div class="plyr__controls">
			<button class="plyr__controls__item plyr__control" type="button" data-plyr="play" aria-label="Play">
				<svg class="icon--pressed" aria-hidden="true" focusable="false"><use xlink:href="#plyr-pause"></use></svg>
				<svg class="icon--not-pressed" aria-hidden="true" focusable="false"><use xlink:href="#plyr-play"></use></svg>
				<span class="label--pressed plyr__tooltip">Pause</span>
				<span class="label--not-pressed plyr__tooltip">Play</span>
				<span class="label--pressed plyr__sr-only">Pause</span>
				<span class="label--not-pressed plyr__sr-only">Play</span>
			</button>
		`;

		if (this.source.controls.includes('progress')) {
			let progress = 	`
			<div class="plyr__controls__item plyr__progress__container">
				<div class="plyr__progress">
					<input data-plyr="seek" type="range" min="0" max="100" step="0.01" value="0" aria-label="Seek">
					<progress class="plyr__progress__buffer" min="0" max="100" value="0">% buffered</progress>
					<span role="tooltip" class="plyr__tooltip">00:00</span>
				</div>
			</div>`
			controls = controls + progress;
		}
		if (this.source.source_alt) {
			let audioDesc = 	`
			<button class="plyr__controls__item plyr__control" type="button" data-plyr="audio-desc">
				<span class="icon icon--not-pressed icon-audio-desc" aria-hidden="true" focusable="false"></span>
				<span class="label--pressed plyr__tooltip plyr__tooltip--right-aligned">Disable audio description</span>
				<span class="label--not-pressed plyr__tooltip plyr__tooltip--right-aligned">Enable audio description</span>
				<span class="label--pressed plyr__sr-only">Disable Audio Description</span>
				<span class="label--not-pressed plyr__sr-only">Enable Audio Description</span>
			</button>`
			controls = controls + audioDesc;
		}

		let captions = `
		<button class="plyr__controls__item plyr__control" type="button" data-plyr="captions">
			<svg class="icon--pressed" aria-hidden="true" focusable="false"><use xlink:href="#plyr-captions-on"></use></svg>
			<svg class="icon--not-pressed" aria-hidden="true" focusable="false"><use xlink:href="#plyr-captions-off"></use></svg>
			<span class="label--pressed plyr__tooltip">Disable captions</span>
			<span class="label--not-pressed plyr__tooltip">Enable captions</span>
			<span class="label--pressed plyr__sr-only">Disable captions</span>
			<span class="label--not-pressed plyr__sr-only">Enable captions</span>
		</button>`
		controls = controls + captions;

		if (this.source.controls.includes('fullscreen')) {
			let fullscreen = 	`
			<button class="plyr__controls__item plyr__control" type="button" data-plyr="fullscreen">
				<svg class="icon--pressed" aria-hidden="true" focusable="false"><use xlink:href="#plyr-exit-fullscreen"></use></svg>
				<svg class="icon--not-pressed" aria-hidden="true" focusable="false"><use xlink:href="#plyr-enter-fullscreen"></use></svg>
				<span class="label--pressed plyr__tooltip">Exit Fulscreen</span>
				<span class="label--not-pressed plyr__tooltip">Fulscreen</span>
				<span class="label--pressed plyr__sr-only">Exit fullscreen</span>
				<span class="label--not-pressed plyr__sr-only">Enter fullscreen</span>
			</button>`
			controls = controls + fullscreen;
		}
		
		return controls;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const players = Array.from(document.querySelectorAll('.plyr-player'));
	players.map(player => new PlyrRunner(player));
});